import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DaypopBrand, DaypopLogo } from '../components/WaypointLogo';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

function formatDate(raw) {
  if (!raw) return '';
  const s = String(raw).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const typeBadge = {
  win:       { label: 'Win',       cls: 'bg-teal-light text-[var(--type-win)]' },
  Win:       { label: 'Win',       cls: 'bg-teal-light text-[var(--type-win)]' },
  setback:   { label: 'Setback',   cls: 'bg-amber-light text-[var(--type-setback)]' },
  Setback:   { label: 'Setback',   cls: 'bg-amber-light text-[var(--type-setback)]' },
  meeting:   { label: 'Meeting',   cls: 'bg-teal-light text-[var(--type-meeting)]' },
  Meeting:   { label: 'Meeting',   cls: 'bg-teal-light text-[var(--type-meeting)]' },
  idea:      { label: 'Idea',      cls: 'bg-lavender-light text-[var(--type-idea)]' },
  Idea:      { label: 'Idea',      cls: 'bg-lavender-light text-[var(--type-idea)]' },
  milestone: { label: 'Milestone', cls: 'bg-sage-light text-[var(--type-milestone)]' },
  Milestone: { label: 'Milestone', cls: 'bg-sage-light text-[var(--type-milestone)]' },
  neutral:   { label: 'Note',      cls: 'bg-bg-surface-alt text-[var(--type-neutral)]' },
  Note:      { label: 'Note',      cls: 'bg-bg-surface-alt text-[var(--type-neutral)]' },
};

const isMilestone = (t) => t === 'milestone' || t === 'Milestone';

function ShowcasePage() {
  const { username } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE}/api/showcase/${encodeURIComponent(username || '')}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          throw new Error(body.detail || `Error ${res.status}`);
        }
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-base flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-bg-base flex flex-col items-center justify-center gap-4 px-6">
        <DaypopLogo size={48} />
        <p className="text-text-secondary text-center">{error}</p>
        <Link to="/" className="text-gold hover:text-gold-muted text-sm font-medium">Go home</Link>
      </div>
    );
  }

  if (!data?.journey_public) {
    return (
      <div className="min-h-screen bg-bg-base flex flex-col items-center justify-center gap-4 px-6">
        <DaypopLogo size={48} />
        <h2 className="font-display text-2xl font-bold text-text-primary">Journey is private</h2>
        <p className="text-text-secondary text-center max-w-md">
          @{username} hasn't made their journey public yet.
        </p>
        <Link to="/" className="text-gold hover:text-gold-muted text-sm font-medium">
          Start your own journey
        </Link>
      </div>
    );
  }

  const { name, bio, position, stats, entries, goals, lessons, decisions, chapters } = data;

  // Merge all items into a single chronological timeline
  const timelineItems = [];
  (entries || []).forEach((e) => timelineItems.push({ ...e, _kind: 'entry', _date: e.date }));
  (goals || []).forEach((g) => timelineItems.push({ ...g, _kind: 'goal', _date: g.start_date || g.created_at }));
  (lessons || []).forEach((l) => timelineItems.push({ ...l, _kind: 'lesson', _date: l.date_learned }));
  (decisions || []).forEach((d) => timelineItems.push({ ...d, _kind: 'decision', _date: d.decision_date }));
  timelineItems.sort((a, b) => (b._date || '').localeCompare(a._date || ''));

  const initials = (name || username || '?')
    .split(/\s+/)
    .map((n) => n[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  const currentChapter = chapters && chapters.length > 0 ? chapters[0] : null;

  return (
    <div className="min-h-screen bg-bg-base">
      <div className="max-w-[680px] mx-auto px-5 py-10 lg:py-16">
        {/* Header */}
        <header className="text-center pb-8 border-b border-border mb-10 animate-fade-up">
          <div className="w-[72px] h-[72px] bg-gold rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold">
            {initials}
          </div>
          <h1 className="font-display text-2xl font-bold text-text-primary mb-1">{name || username}</h1>
          {position && <p className="text-sm text-text-secondary mb-1">{position}</p>}
          {bio && <p className="text-sm text-text-tertiary max-w-md mx-auto">{bio}</p>}
          {currentChapter && (
            <div className="inline-block mt-3 px-4 py-1 bg-gold-light rounded-full text-xs text-gold-muted font-medium">
              Chapter: {currentChapter.title}
            </div>
          )}

          {/* Stats */}
          <div className="flex justify-center gap-10 mt-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-gold font-mono">{stats?.entries || 0}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Entries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gold font-mono">{stats?.lessons || 0}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Lessons</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gold font-mono">{stats?.goals_achieved || 0}</div>
              <div className="text-[11px] text-text-tertiary uppercase tracking-wider">Goals Hit</div>
            </div>
          </div>
        </header>

        {/* Timeline */}
        {timelineItems.length > 0 ? (
          <div className="relative pl-8 animate-fade-up" style={{ animationDelay: '150ms' }}>
            {/* Vertical line */}
            <div className="absolute left-[7px] top-0 bottom-0 w-[2px] bg-gradient-to-b from-gold via-gold-light to-gold" />

            <div className="space-y-5">
              {timelineItems.map((item, idx) => {
                if (item._kind === 'entry') return <EntryNode key={item.id || idx} entry={item} />;
                if (item._kind === 'goal') return <GoalNode key={item.id || idx} goal={item} />;
                if (item._kind === 'lesson') return <LessonNode key={item.id || idx} lesson={item} />;
                if (item._kind === 'decision') return <DecisionNode key={item.id || idx} decision={item} />;
                return null;
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-text-tertiary">No showcase entries yet.</p>
          </div>
        )}

        {/* Footer */}
        <footer className="text-center pt-10 mt-10 border-t border-border">
          <div className="text-sm text-text-tertiary mb-3">Built with</div>
          <div className="flex justify-center mb-4">
            <DaypopBrand logoSize={32} wordmarkClassName="font-display text-lg font-bold text-text-primary" />
          </div>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 bg-gold text-white rounded-xl text-sm font-medium hover:bg-gold-muted transition-colors"
          >
            Start Your Journey
          </Link>
        </footer>
      </div>
    </div>
  );
}

function EntryNode({ entry }) {
  const badge = typeBadge[entry.entry_type] || typeBadge.neutral;
  const milestone = isMilestone(entry.entry_type);

  return (
    <div className={`relative rounded-xl p-4 border transition-colors ${
      milestone
        ? 'bg-gradient-to-br from-[#fffdf5] to-[#fff8e1] border-gold'
        : 'bg-bg-surface border-border hover:border-gold/40'
    }`}>
      {/* Dot */}
      <div className={`absolute -left-[25px] top-[18px] rounded-full border-2 border-gold ${
        milestone ? 'w-4 h-4 bg-gold -left-[27px]' : 'w-3 h-3 bg-white'
      }`} />
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${badge.cls}`}>
          {badge.label}
        </span>
        <span className="text-[11px] text-text-tertiary font-mono">{formatDate(entry.date)}</span>
      </div>
      {entry.title && (
        <h3 className={`font-display font-bold text-text-primary mb-1 ${milestone ? 'text-base' : 'text-sm'}`}>
          {entry.title}
        </h3>
      )}
      {entry.body && (
        <p className="text-sm text-text-secondary leading-relaxed line-clamp-4">{entry.body}</p>
      )}
    </div>
  );
}

function GoalNode({ goal }) {
  const progress = Math.min(100, goal.progress || 0);

  return (
    <div className="relative bg-bg-surface rounded-xl p-4 border border-border hover:border-gold/40 transition-colors">
      <div className="absolute -left-[25px] top-[18px] w-3 h-3 rounded-full border-2 border-gold bg-white" />
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-sage-light text-sage">
          Goal
        </span>
        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded ${
          goal.status === 'achieved' ? 'bg-gold-light text-gold' : 'bg-bg-surface-alt text-text-tertiary'
        }`}>
          {goal.status === 'achieved' ? 'Achieved' : 'Active'}
        </span>
      </div>
      <h3 className="text-sm font-bold text-text-primary mb-2">{goal.title}</h3>
      <div className="h-1.5 bg-sage-light/30 rounded-full overflow-hidden">
        <div className="h-full bg-sage rounded-full transition-all" style={{ width: `${progress}%` }} />
      </div>
    </div>
  );
}

function LessonNode({ lesson }) {
  return (
    <div className="relative bg-bg-surface rounded-xl p-4 border border-border hover:border-gold/40 transition-colors">
      <div className="absolute -left-[25px] top-[18px] w-3 h-3 rounded-full border-2 border-gold bg-white" />
      <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-lavender-light text-lavender mb-2 inline-block">
        Lesson
      </span>
      <div className="mt-1.5 pl-3 border-l-[3px] border-gold">
        <p className="text-sm text-text-secondary italic leading-relaxed">&ldquo;{lesson.lesson_text}&rdquo;</p>
      </div>
      <p className="text-[11px] text-text-tertiary font-mono mt-2">{formatDate(lesson.date_learned)}</p>
    </div>
  );
}

function DecisionNode({ decision }) {
  return (
    <div className="relative bg-bg-surface rounded-xl p-4 border border-border hover:border-gold/40 transition-colors">
      <div className="absolute -left-[25px] top-[18px] w-3 h-3 rounded-full border-2 border-gold bg-white" />
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded bg-teal-light text-teal">
          Decision
        </span>
        <span className="text-[11px] text-text-tertiary font-mono">{formatDate(decision.decision_date)}</span>
      </div>
      <h3 className="text-sm font-bold text-text-primary mb-1">{decision.what_decided}</h3>
      {decision.outcome && (
        <p className="text-sm text-text-secondary">{decision.outcome}</p>
      )}
    </div>
  );
}

export default ShowcasePage;
